import React, { useState, useEffect } from "react";
import { useCart } from "../../contexts/CartContext";
import CartCard from "../../components/controls/CartCard";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { addNewOrder } from "../../db/Orders";
import { CreateOrderDto } from "../../Models/CreateOrderDto";
import { useAuth } from "../../contexts/AuthContext";

const CartPage = ({ eventData }) => {
  const { cart, handleRemoveFromCart } = useCart();
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [errors, setErrors] = useState({});
  const [orderId, setOrderId] = useState("");
  const [savedOrder, setSavedOrder] = useState({
    orderId: "",
    subtotal: 0,
    handlingFees: 0,
    TPS: 0,
    TVQ: 0,
    total: 0,
    userId: "",
  });

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

  const selectedProducts = cart.filter((item) => item.type === "product");
  const selectedServices = cart.filter((item) => item.type === "service");

  const createOrderItems = selectedProducts.map((product) => {
    return {
      ProductId: product.id,
      Quantity: product.quantity || 1,
      UnitPrice: product.price || 0,
      Discount: product.discount || 0,
      SelectedOptionId: product.selectedOptionId || null,
    };
  });

  const createOrderItemsFromServices = selectedServices.map((service) => {
    return {
      ServiceId: service.id,
      Quantity: service.quantity || 1,
      UnitPrice: service.price || 0,
      Discount: service.discount || 0,
    };
  });

  const allOrderItems = [...createOrderItems, ...createOrderItemsFromServices];

  useEffect(() => {
    const createNewOrder = async () => {
      try {
        const dateTimeString = `${eventData.dates[0].date}T${eventData.dates[0].time}:00`;

        const date = new Date(dateTimeString);
        const newOrder = new CreateOrderDto(
          userId,
          eventData.name,
          date,
          eventData.attendees,
          eventData.budget,
          eventData.location,
          eventData.totalAmount,
          allOrderItems,
          eventData.contactPhone
        );

        const response = await addNewOrder(newOrder);
        setOrderId(response.orderId);
        setSavedOrder(response);
      } catch (err) {
        setErrors(err.message);
      } finally {
        setLoading(false);
      }
    };

    createNewOrder();
  }, []);

  const handleCheckoutClick = async () => {
    setLoading(true);

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/create-checkout-session/${orderId}/${userId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    const session = await response.json();
    const stripe = await stripePromise;
    const { error } = await stripe.redirectToCheckout({
      sessionId: session.sessionId,
    });

    if (error) {
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <div className="container mx-auto px-6 py-6 text-gray-600">
      <h1 className="text-2xl font-bold mb-6">{t("cart.cartTitle")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des produits et services */}
        <div className="lg:col-span-2">
          {/* Services */}
          <div>
            <h2 className="text-lg font-bold mb-4">
              {t("cart.servicesTitle")}
            </h2>
            {selectedServices.length > 0 ? (
              selectedServices.map((service) => (
                <CartCard
                  key={service.id}
                  item={service}
                  onRemove={() =>
                    handleRemoveFromCart(
                      cart.findIndex((item) => item.id === service.id)
                    )
                  }
                />
              ))
            ) : (
              <p>{t("cart.noServicesInCart")}</p>
            )}
          </div>

          {/* Produits */}
          <div>
            <h2 className="text-lg font-bold mb-4">
              {t("cart.productsTitle")}
            </h2>
            {selectedProducts.length > 0 ? (
              selectedProducts.map((product) => (
                <CartCard
                  key={product.id}
                  item={product}
                  onRemove={() =>
                    handleRemoveFromCart(
                      cart.findIndex((item) => item.id === product.id)
                    )
                  }
                />
              ))
            ) : (
              <p>{t("cart.noProductsInCart")}</p>
            )}
          </div>
        </div>

        <div className="bg-gray-100 p-6 rounded-lg shadow-md space-y-6">
          <div>
            <div className="text-base font-medium my-2">
              {t("cart.subTotal")} ${savedOrder.subtotal?.toFixed(2) || 0}
            </div>
            {savedOrder.handlingFees > 0 && (
              <div className="text-base">
                {t("cart.managementFees")} $
                {savedOrder.handlingFees.toFixed(2) || 0}
              </div>
            )}
            <div className="text-base">
              {t("cart.TPS")} ${savedOrder.tps?.toFixed(2) || 0}
            </div>
            <div className="text-base">
              {t("cart.TVQ")} ${savedOrder.tvq?.toFixed(2) || 0}
            </div>
            <h2 className="text-lg font-bold my-2">
              {t("cart.totalPrice")} ${savedOrder.total?.toFixed(2) || 0}
            </h2>
          </div>

          {/* Méthode de paiement */}
          <div>
            <button
              className="bg-primary text-white px-4 py-2 rounded-full hover:bg-blue-600 w-full"
              onClick={handleCheckoutClick}
            >
              {t("cart.payOnline")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
